import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatAUD, formatErr } from "@/lib/api";
import { Save, FileText, Upload, Trash2, Plus, Search, ArrowLeft, Image as ImageIcon, Send, UserPlus, Copy, Wand2, Activity } from "lucide-react";
import { toast } from "sonner";
import SchematicBuilder from "@/components/SchematicBuilder";
import { lazy, Suspense } from "react";
const Luxe3DViewer = lazy(() => import("@/three/Luxe3DViewer"));
import ConceptVersionsPanel from "@/components/ConceptVersionsPanel";
import CustomScopeItemsPanel from "@/components/CustomScopeItemsPanel";
import AuthedImage from "@/components/AuthedImage";
import DesignOptionsBar, {
  ReadOnlyBanner, SaveOptionModal, OptionsManagerDrawer, CompareOptionsModal,
} from "@/components/DesignOptionsBar";
import {
  RenderEnvironmentSelector, RenderReadinessPanel,
} from "@/components/PhaseTwoAiiPanels";
import ProjectPricingPanel from "@/components/ProjectPricingPanel";
import ProjectStageHeader, { computeProjectStageState } from "@/components/ProjectStageHeader";
import ProjectSummaryStrip from "@/components/ProjectSummaryStrip";
import ProjectStageSection from "@/components/ProjectStageSection";
import StartDesignEmptyState from "@/components/StartDesignEmptyState";
import DesignTemplateGallery from "@/components/DesignTemplateGallery";
import ApprovalStagePanel from "@/components/ApprovalStagePanel";

const STATUSES = ["draft", "sent", "accepted", "declined"];
const ROOM_TYPES = ["Living Room", "Media Room", "Bedroom", "Office", "Custom"];
const DELIVERY_STAGES = ["Consultation", "Concept Design", "Design Review", "Specification", "Estimating", "Proposal", "Awaiting Approval", "Deposit", "Design Development", "Procurement", "Production", "Site Preparation", "Installation", "Completion", "Closed"];

export default function ProjectBuilderPage() {
  const { id } = useParams();
  const nav = useNavigate();
  // Client Preview mode — when a Studio user opens
  //   /projects/{id}?client_preview=1
  // the workspace mounts with Client View already ON so the design
  // presentation is safe from the first paint. Internal information
  // (Section 07 Pricing, Section 08 Activity, AI Render Package
  // controls, supplier costs, contractor rates, margins) is removed
  // from the DOM entirely rather than hidden with CSS.
  const clientPreviewLock = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("client_preview") === "1";
  const [project, setProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  // Phase 2B-1 — 2D/3D view toggle. 3D consumes the same canonical
  // DesignScene; there is no separate 3D-editable design.
  const [designView, setDesignView] = useState("2d");
  const [productCat, setProductCat] = useState("all");
  const [genPdf, setGenPdf] = useState(false);
  const saveTimer = useRef(null);

  // Phase 1A-S: Investment/commercial privacy toggles.
  // • Investment panel hidden by default when workspace opens.
  // • Show Investment reveals CLIENT-SAFE totals only.
  // • Internal Costing reveals sensitive lines (labour rate, margin,
  //   supplier trade prices).
  // • Client View is a workspace-wide "handing the iPad to the client"
  //   mode. When ON, sensitive controls are hidden regardless of the
  //   Investment toggle. These are UI state ONLY — not persisted in
  //   the scene hash.
  const [showInvestment, setShowInvestment] = useState(false);
  const [internalCosting, setInternalCosting] = useState(false);
  // Client View defaults ON when entering the client preview route so
  // there is zero flash of internal Studio UI before the safe mode
  // engages. On the plain Studio route the default remains OFF.
  const [clientView, setClientView] = useState(clientPreviewLock);

  // ── Design stage empty state (Prompt 3C.3) ──
  // When the schematic has zero items the DESIGN stage renders the
  // Amber-inspired "Start a design" panel. Clicking Blank Design flips
  // this flag so the SchematicBuilder mounts on the empty wall.
  const [startBlankNow, setStartBlankNow] = useState(false);
  // Prompt 4E: gallery-open state for Start From Template.
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);

  // ── Design Options (scene_versions) state ──
  const [sceneVersions, setSceneVersions] = useState([]);
  const [customScopeList, setCustomScopeList] = useState([]);
  const [activeOptionId, setActiveOptionId] = useState(null);
  const [viewedOption, setViewedOption] = useState(null);   // frozen doc when viewing
  const [showSaveOption, setShowSaveOption] = useState(false);
  const [showOptionsManager, setShowOptionsManager] = useState(false);
  const [showCompareOptions, setShowCompareOptions] = useState(false);

  const reloadSceneVersions = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/projects/${id}/scene-versions`);
      setSceneVersions(data || []);
    } catch { /* silent */ }
    try {
      const { data } = await api.get(`/projects/${id}/custom-scope-items`);
      setCustomScopeList(data || []);
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => { reloadSceneVersions(); }, [reloadSceneVersions]);

  const saveOption = async ({ name, description, status, custom_scope_ids }) => {
    try {
      const { data } = await api.post(`/projects/${id}/scene-versions`, { name, description, status, custom_scope_ids });
      toast.success(`Design Option V${data.version_number} saved`);
      await reloadSceneVersions();
      setShowSaveOption(false);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const viewOption = async (opt) => {
    try {
      const { data } = await api.get(`/projects/${id}/scene-versions/${opt.id}`);
      setViewedOption(data);
      setActiveOptionId(opt.id);
      setShowOptionsManager(false);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exitReadOnly = () => { setViewedOption(null); setActiveOptionId(null); };

  const restoreOption = async (opt) => {
    if (!window.confirm(`Replace the Current Design with a copy of V${opt.version_number} — ${opt.name}? The saved Design will remain unchanged.`)) return;
    try {
      await api.post(`/projects/${id}/scene-versions/${opt.id}/restore-to-working`);
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      toast.success(`V${opt.version_number} restored as Current Design`);
      exitReadOnly();
      setShowOptionsManager(false);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const duplicateOption = async (opt) => {
    try {
      await api.post(`/projects/${id}/scene-versions/${opt.id}/duplicate`, {});
      toast.success(`Duplicated V${opt.version_number}`);
      await reloadSceneVersions();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const renameOption = async (opt) => {
    const name = window.prompt("New name", opt.name);
    if (!name || name.trim() === opt.name) return;
    try {
      await api.patch(`/projects/${id}/scene-versions/${opt.id}`, { name: name.trim() });
      await reloadSceneVersions();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const changeOptionStatus = async (opt) => {
    const options = ["saved", "client_option", "approved"];
    const next = window.prompt(`Change status (${options.join(", ")})`, opt.status);
    if (!next || !options.includes(next.trim())) return;
    try {
      await api.patch(`/projects/${id}/scene-versions/${opt.id}`, { status: next.trim() });
      await reloadSceneVersions();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const archiveOption = async (opt) => {
    try {
      await api.post(`/projects/${id}/scene-versions/${opt.id}/archive`);
      await reloadSceneVersions();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const unarchiveOption = async (opt) => {
    try {
      await api.post(`/projects/${id}/scene-versions/${opt.id}/unarchive`);
      await reloadSceneVersions();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Load
  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => nav("/projects"));
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, [id, nav]);

  // Phase 1: proposals, invitations, activities, design versions
  const [proposals, setProposals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [designVersions, setDesignVersions] = useState([]);
  const [magicLink, setMagicLink] = useState(null);
  const loadPhase1 = useCallback(() => {
    api.get(`/projects/${id}/proposals`).then((r) => setProposals(r.data)).catch(() => {});
    api.get(`/projects/${id}/portal/invitations`).then((r) => setInvitations(r.data)).catch(() => {});
    api.get(`/activities?project_id=${id}&limit=50`).then((r) => setActivities(r.data)).catch(() => {});
    api.get(`/projects/${id}/design-versions`).then((r) => setDesignVersions(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { loadPhase1(); }, [loadPhase1]);

  const createProposalVersion = async () => {
    try {
      await doSave(false);
      const { data } = await api.post(`/projects/${id}/proposals/new-version`);
      toast.success(`Proposal V${data.version.version} created`);
      loadPhase1();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const issueProposal = async (vid) => {
    try { await api.post(`/proposal-versions/${vid}/issue`); toast.success("Proposal issued"); loadPhase1(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const downloadVersionPdf = async (v) => {
    try {
      const res = await api.get(`/proposal-versions/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `proposal-v${v.version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const invitePortal = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/portal/invite`);
      setMagicLink(data.magic_link);
      toast.success("Portal invitation created");
      loadPhase1();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const revokeInv = async (iid) => {
    try { await api.post(`/portal-invitations/${iid}/revoke`); toast.success("Access revoked"); loadPhase1(); }
    catch { toast.error("Could not revoke"); }
  };
  const resendInv = async (iid) => {
    try {
      const { data } = await api.post(`/portal-invitations/${iid}/resend`);
      setMagicLink(data.magic_link);
      toast.success("Invitation resent (new link generated)");
      loadPhase1();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const syncSchematicItem = async (schematic_item_id) => {
    try {
      const { data } = await api.post(`/projects/${id}/schematic/${schematic_item_id}/sync-to-estimate`);
      toast.success(`Synced to estimate (${data.quantity} ${data.unit})`);
      // Only refresh line_items (the pricing panel needs the new row).
      // Do NOT re-hydrate schematic.items — that would rewrite the
      // SchematicBuilder's items prop and cascade an autosave that
      // recomputes the scene_hash against the freshly-stamped
      // scene_hash_at_last_estimate_sync, flipping the pill back to
      // "Estimate needs sync" for no good reason.
      const fresh = await api.get(`/projects/${id}`);
      setProject((p) => ({
        ...p,
        line_items: fresh.data.line_items,
        schematic: {
          ...(p?.schematic || {}),
          scene_hash: fresh.data.schematic?.scene_hash,
          scene_hash_at_last_estimate_sync: fresh.data.schematic?.scene_hash_at_last_estimate_sync,
          // items intentionally NOT replaced — preserve the local reference.
        },
      }));
      loadPhase1();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setField = (k, v) => { setProject((p) => ({ ...p, [k]: v })); setDirty(true); };

  // Debounced auto-save
  useEffect(() => {
    if (!project || !dirty) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(false), 1500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [project, dirty]); // eslint-disable-line

  const doSave = async (manual = true) => {
    if (!project) return;
    setSaving(true);
    try {
      const body = {
        title: project.title, status: project.status, client_id: project.client_id,
        room_type: project.room_type, notes: project.notes,
        wall_width_mm: project.wall_width_mm, wall_height_mm: project.wall_height_mm, wall_depth_mm: project.wall_depth_mm,
        schematic: project.schematic || { items: [] },
        line_items: project.line_items || [],
        labour_rate: project.labour_rate, margin_rate: project.margin_rate, tax_rate: project.tax_rate, discount: project.discount,
      };
      const { data } = await api.put(`/projects/${id}`, body);
      setProject(data);
      setDirty(false);
      if (manual) toast.success("Project saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  // Line items
  const addProductLine = (p) => {
    const li = {
      id: `li_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      product_id: p.id, name: p.name, description: p.description, category: p.category,
      quantity: 1, unit: p.unit, unit_price: p.unit_price, image_path: p.image_path, sort_order: (project.line_items || []).length,
    };
    setField("line_items", [...(project.line_items || []), li]);
  };
  const updateLine = (idx, patch) => {
    const next = [...project.line_items];
    next[idx] = { ...next[idx], ...patch };
    setField("line_items", next);
  };
  const removeLine = (idx) => {
    const next = project.line_items.filter((_, i) => i !== idx);
    setField("line_items", next);
  };

  // Images
  const imgInput = useRef();
  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "projects");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${id}/images`, { storage_path: data.storage_path, caption: "" });
      const fresh = await api.get(`/projects/${id}`);
      setProject((p) => ({ ...p, images: fresh.data.images }));
      toast.success("Image added");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeImage = async (imgId) => {
    await api.delete(`/projects/${id}/images/${imgId}`);
    const fresh = await api.get(`/projects/${id}`);
    setProject((p) => ({ ...p, images: fresh.data.images }));
  };

  const generatePdf = async () => {
    setGenPdf(true);
    try {
      await doSave(false);
      await api.post(`/projects/${id}/proposal/pdf`);
      toast.success("Proposal PDF generated");
      // open the proposal preview
      nav(`/projects/${id}/proposal`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setGenPdf(false); }
  };

  if (!project) return <div className="p-12 text-muted-foreground">Loading…</div>;

  // Live totals
  const totals = computeTotals(project);

  // ── Six-stage state (Prompt 3C.3) ────────────────────────────────
  // Shared between the ProjectStageHeader (pills + Next Action),
  // ProjectSummaryStrip and per-stage dividers so every surface reads
  // exactly the same current/done state.
  const stageState = computeProjectStageState(project, sceneVersions, proposals, activities);
  const stageStateOf = (key) => {
    if (stageState.done[key]) return "done";
    if (stageState.current === key) return "current";
    return "pending";
  };
  const items = (project.schematic || {}).items || [];

  // Studio proposal actions block (extracted so it can be relocated
  // into the Proposal stage without duplicating markup).
  const renderStudioProposalActions = () => (
    <section className="luxe-card p-6 grid md:grid-cols-3 gap-3" data-testid="studio-actions">
      <div className="md:col-span-3 overline mb-1">Actions</div>
      <button data-testid="btn-create-proposal-version" onClick={createProposalVersion} className="btn-outline justify-center">
        <FileText className="w-4 h-4" strokeWidth={1.5}/> Create Proposal {proposals[0]?.versions?.length ? `V${(proposals[0].versions[0]?.version||0)+1}` : "V1"}
      </button>
      <button data-testid="btn-invite-portal" onClick={invitePortal} className="btn-outline justify-center">
        <UserPlus className="w-4 h-4" strokeWidth={1.5}/> Send Portal Invite
      </button>
      <select data-testid="select-delivery-stage" value={project.delivery_stage || "Consultation"} onChange={(e) => setField("delivery_stage", e.target.value)} className="luxe-input">
        {DELIVERY_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      {magicLink && (
        <div className="md:col-span-3 border-l-2 border-[color:hsl(var(--accent))] bg-[color:hsl(var(--surface-panel))] p-4 mt-3" data-testid="magic-link-block">
          <div className="overline text-[10px]" style={{ color: "#A68A64" }}>MOCKED EMAIL DELIVERY — staging only</div>
          <div className="font-serif text-lg mt-2">Portal invitation link</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <input readOnly value={magicLink} className="luxe-input flex-1 text-xs" data-testid="magic-link-input"/>
            <button data-testid="btn-copy-magic-link" onClick={async () => {
              try {
                if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(magicLink);
                } else {
                  const ta = document.createElement("textarea");
                  ta.value = magicLink; document.body.appendChild(ta);
                  ta.select(); document.execCommand("copy"); ta.remove();
                }
                toast.success("Copied");
              } catch { toast.error("Copy failed — please copy manually from the field"); }
            }} className="btn-primary">
              <Copy className="w-4 h-4" strokeWidth={1.5}/> Copy Link
            </button>
          </div>
          <div className="text-xs text-muted-foreground mt-2">Real email delivery is not connected in this phase.</div>
        </div>
      )}

      {invitations.length > 0 && (
        <div className="md:col-span-3 mt-3">
          <div className="overline text-[10px] mb-2">Portal invitations</div>
          <div className="space-y-2">
            {invitations.map((iv) => (
              <div key={iv.id} className="flex items-center justify-between text-xs border-b border-border py-2" data-testid={`inv-${iv.id}`}>
                <div>
                  <div>Invited {new Date(iv.invited_at).toLocaleString("en-AU")}</div>
                  <div className="text-muted-foreground">
                    {iv.revoked ? "Revoked" : (iv.first_accessed_at ? `First accessed ${new Date(iv.first_accessed_at).toLocaleString("en-AU")}` : "Not yet accessed")} · Expires {new Date(iv.expires_at).toLocaleDateString("en-AU")}
                  </div>
                </div>
                <div className="flex gap-2">
                  {!iv.revoked && <button data-testid={`btn-resend-${iv.id}`} onClick={() => resendInv(iv.id)} className="btn-outline text-xs">Resend</button>}
                  {!iv.revoked && <button data-testid={`btn-revoke-${iv.id}`} onClick={() => revokeInv(iv.id)} className="btn-outline text-xs text-[color:hsl(var(--destructive))]">Revoke</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {proposals[0]?.versions?.length > 0 && (
        <div className="md:col-span-3 mt-3">
          <div className="overline text-[10px] mb-2">Proposal history</div>
          <div className="space-y-2">
            {proposals[0].versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between text-xs border-b border-border py-2" data-testid={`pv-${v.id}`}>
                <div>
                  <div className="font-medium">V{v.version} · {v.status}</div>
                  <div className="text-muted-foreground">
                    {v.issued_at && `Issued ${new Date(v.issued_at).toLocaleDateString("en-AU")} · `}
                    Total ${(v.totals_cents?.total/100).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`btn-pv-pdf-${v.id}`} onClick={() => downloadVersionPdf(v)} className="btn-outline text-xs">PDF</button>
                  {v.status === "draft" && <button data-testid={`btn-pv-issue-${v.id}`} onClick={() => issueProposal(v.id)} className="btn-primary text-xs">Issue</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-[1400px]" data-testid="project-builder">
      {/* Header */}
      <button onClick={() => nav("/projects")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to projects
      </button>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div className="flex-1">
          <div className="overline">Project · {project.project_number || ""}</div>
          <input
            data-testid="input-project-title"
            value={project.title}
            onChange={(e) => setField("title", e.target.value)}
            className="font-serif font-light bg-transparent border-none outline-none w-full mt-2"
            style={{ fontSize: "44px", lineHeight: 1.05 }}
          />
          <div className="flex flex-wrap gap-4 items-center mt-3 text-xs text-muted-foreground">
            <span>Last updated {new Date(project.updated_at).toLocaleString("en-AU")}</span>
            {dirty && <span className="text-[color:hsl(var(--accent))]">• Unsaved changes</span>}
            {saving && <span>Saving…</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <select data-testid="select-project-status" value={project.status} onChange={(e) => setField("status", e.target.value)} className="luxe-input">
            {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select data-testid="select-room-type" value={project.room_type || "Living Room"} onChange={(e) => setField("room_type", e.target.value)} className="luxe-input">
            {ROOM_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="btn-save-project" onClick={() => doSave(true)} disabled={saving} className="btn-outline">
            <Save className="w-4 h-4" strokeWidth={1.5}/> Save
          </button>
          <button data-testid="btn-generate-pdf" onClick={generatePdf} disabled={genPdf} className="btn-primary">
            <FileText className="w-4 h-4" strokeWidth={1.5}/> {genPdf ? "Generating…" : "Preview proposal"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Main column — full width; Investment now lives at the bottom */}
        <div className="space-y-10">
          {/* Prompt 4D: Sample-project banner. Only rendered on
              projects flagged is_sample=true and not in Client View. */}
          {!clientView && project.is_sample && (
            <div className="luxe-card p-4 border-l-2 border-[color:hsl(var(--accent))] flex items-start justify-between gap-4"
                 data-testid="sample-project-banner">
              <div>
                <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Sample Project</div>
                <p className="text-sm mt-1 text-muted-foreground max-w-2xl">
                  This is example data to help you explore Subtend Studio. Edit anything freely — none of it counts toward your real pipeline. You can remove it whenever you like.
                </p>
              </div>
              <button data-testid="btn-delete-sample-project"
                      onClick={async () => {
                        if (!window.confirm("Remove the sample project and its sample client? Real projects and starter materials are not affected.")) return;
                        try {
                          await api.delete("/onboarding/sample");
                          toast.success("Sample content removed");
                          nav("/projects");
                        } catch (e) { toast.error(formatErr(e)); }
                      }}
                      className="btn-outline text-xs shrink-0">
                Remove sample content
              </button>
            </div>
          )}
          {/* Prompt 3C.3: Project Summary strip + six-stage header sit
              at the top of the workspace. Studio Actions have been
              relocated into the Proposal stage below. */}
          {!clientView && (
            <ProjectSummaryStrip
              project={project}
              sceneVersions={sceneVersions}
              proposals={proposals}
              activeOptionId={activeOptionId}
              currentStage={stageState.current}
              investmentTotal={totals.total}
              clientView={clientView}
            />
          )}
          {!clientView && (
            <ProjectStageHeader project={project}
                                sceneVersions={sceneVersions}
                                proposals={proposals}
                                activities={activities}/>
          )}
          {/* ─── BRIEF STAGE ─── */}
          <ProjectStageSection stageKey="brief" label="Brief"
                               purpose="What are we designing, for whom, and what space are we working with?"
                               state={stageStateOf("brief")}>
          {/* Client + measurements */}
          <Section step="01" title="Client & Measurements">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="luxe-card p-6">
                <div className="overline mb-3">Client</div>
                <select data-testid="select-client" value={project.client_id || ""} onChange={(e) => setField("client_id", e.target.value || null)} className="luxe-input">
                  <option value="">No client linked</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {project.client && (
                  <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                    <div>{project.client.email}</div>
                    <div>{project.client.phone}</div>
                    <div>{project.client.address}</div>
                  </div>
                )}
              </div>
              <div className="luxe-card p-6">
                <div className="overline mb-3">Wall measurements (mm)</div>
                <div className="grid grid-cols-3 gap-3">
                  <NumField label="Width" testid="input-wall-width" value={project.wall_width_mm} onChange={(v) => setField("wall_width_mm", v)}/>
                  <NumField label="Height" testid="input-wall-height" value={project.wall_height_mm} onChange={(v) => setField("wall_height_mm", v)}/>
                  <NumField label="Depth" testid="input-wall-depth" value={project.wall_depth_mm} onChange={(v) => setField("wall_depth_mm", v)}/>
                </div>
                <div className="mt-4">
                  <div className="overline mb-2">Design notes</div>
                  <textarea data-testid="input-project-notes" rows="2" className="luxe-input" value={project.notes || ""} onChange={(e) => setField("notes", e.target.value)}/>
                </div>
              </div>
            </div>
          </Section>

          {/* Image gallery — kept inside the BRIEF stage: room photos
              and reference images are inputs to what we're designing. */}
          <Section step="05" title="Project Images">
            <input ref={imgInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}/>
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border">
              <button data-testid="btn-upload-project-image" onClick={() => imgInput.current.click()} className="bg-card aspect-square flex flex-col items-center justify-center gap-3 hover:bg-muted transition-colors text-muted-foreground">
                <Upload className="w-5 h-5" strokeWidth={1.5}/>
                <span className="text-xs">Add image</span>
              </button>
              {(project.images || []).map((img) => (
                <div key={img.id} className="bg-card aspect-square relative group" data-testid={`project-image-${img.id}`}>
                  <AuthedImage path={img.storage_path} alt="" className="w-full h-full object-cover"/>
                  <button data-testid={`btn-remove-image-${img.id}`} onClick={() => removeImage(img.id)} className="absolute top-2 right-2 p-1.5 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                  </button>
                </div>
              ))}
            </div>
          </Section>
          </ProjectStageSection>

          {/* ─── DESIGN STAGE ─── */}
          <ProjectStageSection stageKey="design" label="Design"
                               purpose="Create and refine the media wall."
                               state={stageStateOf("design")}>
          {items.length === 0 && !startBlankNow ? (
            <StartDesignEmptyState onStartBlank={() => setStartBlankNow(true)}
                                   onStartFromTemplate={() => setTemplateGalleryOpen(true)}/>
          ) : (
          <>
          {/* Products library + line items */}
          <Section step="02" title="Materials & Line Items">
            <div className="grid lg:grid-cols-[300px,1fr] gap-px bg-border border border-border">
              <div className="bg-card p-5 max-h-[500px] overflow-y-auto scrollbar-thin">
                <div className="overline text-[10px] mb-3">Library</div>
                <div className="mb-3">
                  <input data-testid="input-library-search"
                         aria-label="Filter library materials"
                         className="luxe-input text-xs"
                         placeholder="Filter materials"
                         value={productQuery}
                         onChange={(e) => setProductQuery(e.target.value)}/>
                </div>
                <div className="space-y-2">
                  {products
                    .filter((p) => p.name.toLowerCase().includes(productQuery.toLowerCase()))
                    .map((p) => (
                      <button
                        key={p.id}
                        data-testid={`btn-add-product-${p.id}`}
                        onClick={() => addProductLine(p)}
                        className="w-full flex items-center gap-3 p-2 border border-border hover:border-foreground transition-colors text-left">
                        <div className="w-10 h-10 bg-muted shrink-0 overflow-hidden">
                          <AuthedImage path={p.image_path} alt="" className="w-full h-full object-cover" fallback={<div className="text-[10px] text-muted-foreground w-full h-full flex items-center justify-center">·</div>}/>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium truncate">{p.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{p.category} · {formatAUD(p.unit_price)}</div>
                        </div>
                        <Plus className="w-3 h-3 shrink-0 text-muted-foreground" strokeWidth={1.5}/>
                      </button>
                    ))}
                  {products.length === 0 && (
                    <div className="text-xs text-muted-foreground p-3">No materials in library yet. Add some via Materials Library.</div>
                  )}
                </div>
              </div>
              <div className="bg-card overflow-x-auto">
                {(project.line_items || []).length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="font-serif text-xl mb-1">No materials added</div>
                    <div className="text-xs text-muted-foreground">Click materials in the library to add them as line items.</div>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border" style={{ background: "#F4F2EC" }}>
                        <th className="text-left px-4 py-3 overline text-[10px]">Item</th>
                        <th className="text-right px-3 py-3 overline text-[10px]">Qty</th>
                        <th className="text-right px-3 py-3 overline text-[10px]">Unit Price</th>
                        <th className="text-right px-3 py-3 overline text-[10px]">Total</th>
                        <th className="px-2 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.line_items.map((li, idx) => (
                        <tr key={li.id} className="border-b border-border last:border-0" data-testid={`line-item-${li.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-muted shrink-0 overflow-hidden">
                                <AuthedImage path={li.image_path} alt="" className="w-full h-full object-cover"/>
                              </div>
                              <div>
                                <div className="font-medium">{li.name}</div>
                                <div className="text-xs text-muted-foreground">{li.category} · {li.unit}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <input data-testid={`qty-${li.id}`} type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(idx, { quantity: parseFloat(e.target.value) || 0 })} className="luxe-input w-20 text-right"/>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <input data-testid={`price-${li.id}`} type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLine(idx, { unit_price: parseFloat(e.target.value) || 0 })} className="luxe-input w-24 text-right"/>
                          </td>
                          <td className="px-3 py-3 text-right font-medium" data-testid={`linetotal-${li.id}`}>{formatAUD(li.quantity * li.unit_price)}</td>
                          <td className="px-2 py-3 text-right">
                            <button data-testid={`btn-remove-line-${li.id}`} onClick={() => removeLine(idx)} className="p-2 text-muted-foreground hover:text-[color:hsl(var(--destructive))]">
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </Section>

          {/* Schematic — wide workspace: escapes page px-14 padding so
              the canvas gets the full main-column width. Preserves the
              rest of the project sections at readable standard width. */}
          <div className="lg:-mx-14" data-testid="design-workspace-wide">
            <Section step="03" title="Design workspace">
              <div className="space-y-2">
                <DesignOptionsBar
                  options={sceneVersions}
                  activeOptionId={activeOptionId}
                  onSaveOption={() => setShowSaveOption(true)}
                  onOpenManager={() => setShowOptionsManager(true)}
                  onOpenCompare={() => setShowCompareOptions(true)}
                  onSelectOption={(oid) => viewOption(sceneVersions.find((o) => o.id === oid))}
                  onExitReadOnly={exitReadOnly}
                />
                {viewedOption && <ReadOnlyBanner option={viewedOption} onExit={exitReadOnly}/>}
                <SchematicBuilder
                  key={viewedOption ? `sv-${viewedOption.id}` : "working"}
                  projectId={viewedOption ? null : id}
                  wallW={viewedOption ? viewedOption.wall_width_mm : project.wall_width_mm}
                  wallH={viewedOption ? viewedOption.wall_height_mm : project.wall_height_mm}
                  wallDepth={(viewedOption ? viewedOption.wall_depth_mm : project.wall_depth_mm) || 400}
                  items={viewedOption
                          ? (viewedOption.schematic_snapshot?.items || [])
                          : (project.schematic?.items || [])}
                  onChange={viewedOption ? () => {} : (items) => setField("schematic", { items })}
                  products={products}
                  onSyncItem={viewedOption ? null : syncSchematicItem}
                  readOnly={!!viewedOption}
                  clientView={clientView}
                />
              </div>
            </Section>
          </div>

          {showSaveOption && (
            <SaveOptionModal
              suggestedName={`Option V${sceneVersions.length + 1}`}
              customScope={customScopeList}
              onSave={saveOption}
              onClose={() => setShowSaveOption(false)}
            />
          )}
          {showOptionsManager && (
            <OptionsManagerDrawer
              options={sceneVersions}
              onClose={() => setShowOptionsManager(false)}
              onView={viewOption}
              onRestore={restoreOption}
              onDuplicate={duplicateOption}
              onRename={renameOption}
              onChangeStatus={changeOptionStatus}
              onArchive={archiveOption}
              onUnarchive={unarchiveOption}
            />
          )}
          {showCompareOptions && (
            <CompareOptionsModal
              options={sceneVersions}
              onClose={() => setShowCompareOptions(false)}
            />
          )}

          {/* Concept Versioning */}
          <Section step="04" title="Design Concepts">
            <ConceptVersionsPanel projectId={id} designVersions={designVersions} onReload={loadPhase1}/>
          </Section>

          {/* Custom Scope Items (Phase 1A-S) */}
          <Section step="06" title="Custom Scope Items">
            <CustomScopeItemsPanel projectId={id} schematicItems={(project.schematic || {}).items || []}/>
          </Section>
          </>
          )}
          </ProjectStageSection>

          {/* ─── VISUALISE STAGE ─── */}
          <ProjectStageSection stageKey="visualise" label="Visualise"
                               purpose="Review the design in 3D and prepare presentation visuals."
                               state={stageStateOf("visualise")}>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <div className="luxe-card p-4" data-testid="visualise-environment-card">
                <RenderEnvironmentSelector
                  projectId={id}
                  currentKey={project.render_environment || "natural_daylight"}
                  onChange={(k) => setField("render_environment", k)}/>
              </div>
              <div className="luxe-card p-4" data-testid="visualise-readiness-card">
                <RenderReadinessPanel
                  projectId={id}
                  sceneVersionId={activeOptionId}/>
              </div>
            </div>
            <div className="lg:-mx-14 mt-6" data-testid="visualise-3d-panel-wrap">
              <Luxe3DViewerPanel
                project={project}
                scene={viewedOption
                        ? viewedOption.schematic_snapshot
                        : (project.schematic || {})}
                sceneVersion={viewedOption || null}
                envKey={viewedOption ? viewedOption.render_environment_pinned : (project.render_environment || "natural_daylight")}
                clientView={clientView}
              />
            </div>
            <div className="luxe-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6"
                 data-testid="premium-render-cta">
              <div className="min-w-0">
                <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Coming soon</div>
                <div className="font-serif text-lg mt-1">Create premium render</div>
                <div className="text-xs text-muted-foreground mt-1 max-w-xl">
                  Turn a saved design into a photoreal presentation visual. Available in a future release.
                </div>
              </div>
              <button type="button"
                      data-testid="btn-create-premium-render"
                      disabled
                      aria-disabled="true"
                      className="btn-outline text-xs opacity-60 cursor-not-allowed shrink-0">
                Coming soon
              </button>
            </div>
          </ProjectStageSection>

          {/* ─── PRICE STAGE ─── (hidden entirely in Client View) */}
          {!clientView && (
          <ProjectStageSection stageKey="price" label="Price"
                               purpose="Review cost, sell price and margin."
                               state={stageStateOf("price")}>
            <PriceSummaryCard totals={totals} hasEstimate={items.length > 0 || (project.line_items || []).length > 0}/>
            <Section step="07" title="Pricing details">
              <ProjectPricingPanel projectId={id} sceneVersions={sceneVersions}/>
            </Section>
          </ProjectStageSection>
          )}

          {/* ─── PROPOSAL STAGE ─── */}
          <ProjectStageSection stageKey="proposal" label="Proposal"
                               purpose="Prepare and send the client proposal."
                               state={stageStateOf("proposal")}>
            {(proposals[0]?.versions?.length || 0) === 0 ? (
              <ProposalEmptyState
                canCreate={items.length > 0 && (project.client_id || null) !== null}
                needsDesign={items.length === 0}
                needsClient={!project.client_id}
                onCreate={createProposalVersion}
              />
            ) : null}
            {!clientView && renderStudioProposalActions()}
            {/* Investment — sits inside the Proposal stage in normal
                document flow. Hidden by default; showInvestment reveals
                CLIENT-SAFE totals only; Internal Costing reveals
                sensitive lines. */}
            <div id="investment-section" data-testid="investment-section-bottom">
              <Section step="09" title="Investment">
              <div className="luxe-card p-6" data-testid="investment-sidebar">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">Investment</div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="btn-toggle-client-view"
                      onClick={() => { if (!clientPreviewLock) setClientView((v) => !v); }}
                      disabled={clientPreviewLock}
                      className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${clientView ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground"} ${clientPreviewLock ? "opacity-60 cursor-not-allowed" : ""}`}
                      title={clientPreviewLock
                        ? "Client Preview route — exit through the Studio to return to admin mode"
                        : "Hide sensitive commercial details — for handing the iPad to the client"}>
                      {clientView ? "Client View · ON" : "Client View"}
                    </button>
                  </div>
                </div>

                {!showInvestment ? (
                  <div className="space-y-3" data-testid="investment-hidden-state">
                    <div className="text-xs text-muted-foreground">
                      Investment is hidden by default when the workspace opens.
                      Pricing still calculates in the background.
                    </div>
                    <button
                      data-testid="btn-show-investment"
                      onClick={() => setShowInvestment(true)}
                      className="btn-outline w-full justify-center text-xs">
                      Show Investment
                    </button>
                    {!clientView && (
                      <button
                        data-testid="btn-toggle-internal-costing"
                        onClick={() => { setShowInvestment(true); setInternalCosting(true); }}
                        className="btn-outline w-full justify-center text-[10px] uppercase tracking-widest">
                        Internal Costing
                      </button>
                    )}
                  </div>
                ) : (
                  <div data-testid="investment-visible-state">
                    <div className="flex justify-end mb-2">
                      <button
                        data-testid="btn-hide-investment"
                        onClick={() => { setShowInvestment(false); setInternalCosting(false); }}
                        className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Hide
                      </button>
                    </div>
                    <div className="space-y-3 text-sm">
                      <Row k="Subtotal" v={formatAUD(totals.subtotal)} testid="total-subtotal"/>
                      {internalCosting && !clientView ? (
                        <>
                          <EditableRate k="Labour" rate={project.labour_rate} onRate={(v) => setField("labour_rate", v)} amount={totals.labour} testid="labour"/>
                          <EditableRate k="Margin" rate={project.margin_rate} onRate={(v) => setField("margin_rate", v)} amount={totals.margin} testid="margin"/>
                        </>
                      ) : (
                        <div className="text-[10px] text-muted-foreground" data-testid="commercial-privacy-notice">
                          Labour, margin & supplier costs hidden.
                          {!clientView && (
                            <button
                              data-testid="btn-reveal-internal-costing"
                              onClick={() => setInternalCosting(true)}
                              className="ml-2 underline">
                              Show Internal Costing
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground text-xs">Discount ($)</span>
                        <input data-testid="input-discount" type="number" step="0.01" value={project.discount} onChange={(e) => setField("discount", parseFloat(e.target.value) || 0)} className="luxe-input w-24 text-right text-xs" disabled={clientView}/>
                      </div>
                      <EditableRate k="GST" rate={project.tax_rate} onRate={(v) => setField("tax_rate", v)} amount={totals.gst} testid="gst" disabled={clientView}/>
                    </div>
                    <div className="border-t border-foreground mt-5 pt-5">
                      <div className="flex items-end justify-between">
                        <div>
                          <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Total (AUD inc. GST)</div>
                        </div>
                      </div>
                      <div className="font-serif font-light mt-1" style={{ fontSize: "36px" }} data-testid="total-grand">
                        {formatAUD(totals.total)}
                      </div>
                    </div>
                    {!clientView && (
                      <button data-testid="btn-generate-pdf-side" onClick={generatePdf} disabled={genPdf} className="btn-accent w-full justify-center mt-6">
                        <FileText className="w-4 h-4" strokeWidth={1.5}/> {genPdf ? "Generating…" : "Preview & export PDF"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              </Section>
            </div>
          </ProjectStageSection>

          {/* ─── APPROVAL STAGE ─── */}
          <ProjectStageSection stageKey="approval" label="Approval"
                               purpose="Review feedback and client acceptance."
                               state={stageStateOf("approval")}>
            <ApprovalStagePanel
              project={project}
              proposals={proposals}
              activities={activities}
              sceneVersions={sceneVersions}
              onReviewDesign={() => document.getElementById("stage-design")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              onOpenProposal={() => document.getElementById("stage-proposal")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              clientView={clientView}
            />
            {!clientView && (
              <Section step="08" title="Activity">
                <div className="luxe-card p-2 max-h-[400px] overflow-y-auto scrollbar-thin" data-testid="activity-feed">
                  {activities.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-4">No activity yet.</div>
                  ) : activities.map((a) => (
                    <div key={a.id} className="flex gap-3 border-b border-border last:border-0 p-3 text-xs">
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: a.actor === "client" ? "#A68A64" : "#1C1C1C" }}/>
                      <div className="flex-1">
                        <div className="text-foreground">{a.description}</div>
                        <div className="text-muted-foreground mt-0.5">{a.actor} · {new Date(a.created_at).toLocaleString("en-AU")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </ProjectStageSection>
        </div>
      </div>

      {/* Prompt 4E — Design Template Gallery modal */}
      {templateGalleryOpen && (
        <DesignTemplateGallery
          project={project}
          onInstantiated={async () => { await loadPhase1(); setStartBlankNow(true); }}
          onClose={() => setTemplateGalleryOpen(false)}
        />
      )}

      {/* Sticky Running Total — compact, client-safe. Hidden by default
          when Client View is ON so the studio can hand the iPad to the
          client without revealing price. */}
      {(!clientView || showInvestment) && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur px-6 py-3 flex items-center justify-between z-30"
             style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
             data-testid="running-total-bar">
          <div className="flex items-center gap-4 min-w-0">
            <div className="overline text-[10px]">Current Investment</div>
            <div className="font-serif text-lg" data-testid="running-total-amount">
              {formatAUD(totals.total)}
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">inc. GST</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {clientView && (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="running-total-client-view-label">
                Client View
              </span>
            )}
            <button
              data-testid="btn-view-investment"
              onClick={() => {
                setShowInvestment(true);
                setTimeout(() => document.getElementById("investment-section")?.scrollIntoView({behavior:"smooth", block:"start"}), 0);
              }}
              className="btn-outline text-xs">
              View Investment
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ step, title, children }) {
  // Prompt 3C.3: the old "Section 0N · Title" scaffolding is no longer
  // exposed. Internal section IDs remain intact so ProjectStageHeader
  // deep links, tests and legacy callers keep working; only the visible
  // overline + numbering is removed. The heading now reads like a
  // sub-heading beneath its parent stage.
  return (
    <section id={`section-${step}`} data-section-step={step} className="scroll-mt-24">
      {title ? (
        <h3 className="font-serif font-light mb-5" style={{ fontSize: "22px", lineHeight: 1.15 }}>{title}</h3>
      ) : null}
      {children}
    </section>
  );
}

// Prompt 3C.3 — compact Cost / Sell / Margin / Estimate Total overview
// that sits at the top of the PRICE stage. Uses the same live totals
// helper as the Investment panel — no calculation drift. Internal
// figures (cost, margin) are only exposed here for studio users; this
// component is rendered only when clientView === false.
function PriceSummaryCard({ totals, hasEstimate }) {
  const cost = Math.max(0, (totals.subtotal || 0) + (totals.labour || 0));
  const sell = Math.max(0, cost + (totals.margin || 0));
  const marginPct = sell > 0 ? ((totals.margin || 0) / sell) * 100 : 0;
  const value = (v, empty = "Not priced yet") => hasEstimate ? formatAUD(v) : empty;
  const cells = [
    { key: "cost",     label: "Cost",            v: value(cost) },
    { key: "sell",     label: "Sell price",      v: value(sell) },
    { key: "margin",   label: "Margin",          v: hasEstimate ? `${marginPct.toFixed(1)}%` : "—" },
    { key: "estimate", label: "Estimate total",  v: value(totals.total) },
  ];
  return (
    <div className="luxe-card p-5" data-testid="price-summary-card">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
        {cells.map((c, idx) => (
          <div key={c.key} className={`min-w-0 ${idx > 0 ? "md:border-l md:border-border/70 md:pl-6" : ""}`}
               data-testid={`price-summary-${c.key}`}>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
            <div className="font-serif text-xl mt-1 truncate">{c.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Prompt 3C.3 — Proposal stage empty state. Shown when no proposal
// version exists yet. Guides the user to any pre-requisites rather
// than presenting a dead "Create Proposal" button.
function ProposalEmptyState({ canCreate, needsDesign, needsClient, onCreate }) {
  return (
    <div className="luxe-card p-8" data-testid="proposal-empty-state">
      <div className="overline text-[10px] text-muted-foreground">No proposal yet</div>
      <div className="font-serif font-light mt-2" style={{ fontSize: "26px", lineHeight: 1.15 }}>
        Send a professional client proposal when your design and pricing are ready.
      </div>
      {canCreate ? (
        <button type="button"
                data-testid="btn-proposal-create-empty"
                onClick={onCreate}
                className="btn-primary text-sm mt-6">
          <FileText className="w-4 h-4" strokeWidth={1.5}/> Create proposal
        </button>
      ) : (
        <div className="mt-6" data-testid="proposal-empty-blockers">
          <div className="text-xs text-muted-foreground mb-2">Before you can create the proposal:</div>
          <ul className="text-sm space-y-1">
            {needsClient && <li data-testid="proposal-blocker-client">• Link a client to this project (in Brief).</li>}
            {needsDesign && <li data-testid="proposal-blocker-design">• Add or restore at least one design.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function NumField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="overline text-[10px] block mb-1" style={{ color: "#8A8A8A" }}>{label}</label>
      <input data-testid={testid} type="number" className="luxe-input" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}/>
    </div>
  );
}

function Row({ k, v, testid }) {
  return <div className="flex justify-between" data-testid={testid}><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}

function EditableRate({ k, rate, onRate, amount, testid, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground text-xs">{k}</span>
        <input data-testid={`input-${testid}-rate`} type="number" step="0.01" disabled={disabled} className="w-14 text-right text-[10px] border border-border bg-transparent px-1 py-0.5 disabled:opacity-40" value={(rate * 100).toFixed(1)} onChange={(e) => onRate((parseFloat(e.target.value) || 0) / 100)}/>
        <span className="text-[10px] text-muted-foreground">%</span>
      </div>
      <span className="font-medium text-sm" data-testid={`amount-${testid}`}>{formatAUD(amount)}</span>
    </div>
  );
}

function computeTotals(p) {
  const items = p.line_items || [];
  const subLi = items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  const schemItems = p.schematic?.items || [];
  const subSch = schemItems.reduce((s, i) => s + (Number(i.price) || 0), 0);
  const subtotal = subLi + subSch;
  const labour = subtotal * (p.labour_rate || 0);
  const margin = (subtotal + labour) * (p.margin_rate || 0);
  const preTax = subtotal + labour + margin - (p.discount || 0);
  const gst = preTax * (p.tax_rate || 0);
  const total = preTax + gst;
  return { subtotal, labour, margin, gst, total };
}


// ── Luxe 3D viewer panel (Phase 2B-1) ────────────────────────────
// Compact wrapper wired to the canonical DesignScene. Fetches pinned
// visual asset pins for a Design Option so Option A never resolves a
// newer Master material asset when V2 is created later.
import { CAMERA_PRESETS } from "@/three/Luxe3DViewer";
import { generateAllPasses } from "@/three/RenderPassManager";

function Luxe3DViewerPanel({ project, scene, sceneVersion, envKey, clientView = false }) {
  const [preset, setPreset] = useState("perspective");
  const [pinsByMaterialId, setPins] = useState({});
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState(null);
  const stackRef = useRef({ renderer: null, scene: null, camera: null });
  const onReady = useCallback((s) => { stackRef.current = s; }, []);
  // Compose {mid: pinRecord} from the Design Option's
  // visual_asset_pins so the Material3DResolver walks V-frozen assets.
  useEffect(() => {
    if (!sceneVersion) { setPins({}); return; }
    const map = {};
    (sceneVersion.visual_asset_pins || []).forEach((p) => { map[p.material_id] = p; });
    setPins(map);
  }, [sceneVersion?.id, sceneVersion?.visual_asset_pins?.length]);
  const envProject = { ...(project || {}), render_environment: envKey };

  async function buildPackage() {
    if (!sceneVersion?.id) {
      toast.error("Save the design first — a saved Design is required before preparing a render.");
      return;
    }
    const stack = stackRef.current;
    if (!stack.renderer || !stack.scene || !stack.camera) {
      toast.error("3D viewer isn't ready yet.");
      return;
    }
    setBusy(true);
    try {
      const width = 1024, height = 640;
      // 1. Create the Render Package on the backend — freezes manifest.
      const cameraSnapshot = {
        preset,
        eye: stack.camera.position.toArray(),
        fov: stack.camera.isPerspectiveCamera ? stack.camera.fov : null,
        near: stack.camera.near, far: stack.camera.far,
        aspect: stack.camera.aspect,
      };
      const created = await api.post(
        `/projects/${project.id}/scene-versions/${sceneVersion.id}/render-packages`,
        {
          camera: { preset, fov: cameraSnapshot.fov },
          output: { width, height, aspect_ratio: width / height },
          camera_snapshot: cameraSnapshot,
        }
      );
      const pkgId = created.data.id;
      // 2. Generate all 6 pixel-aligned passes from ONE frozen camera.
      const result = await generateAllPasses(
        stack.renderer, stack.scene, stack.camera, width, height,
      );
      // 3. Upload each pass sequentially.
      const passNames = ["beauty", "depth", "normal", "object_id", "material_id", "emissive"];
      for (const name of passNames) {
        const blob = result.passes[name];
        if (!blob) continue;
        const form = new FormData();
        form.append("file", blob, `${name}.png`);
        form.append("pass_name", name);
        form.append("width", String(width));
        form.append("height", String(height));
        form.append("format", "png");
        form.append("encoding", name === "depth" ? "rgba_depth_packed"
                              : name === "normal" ? "world_normal_rgb"
                              : (name === "object_id" || name === "material_id") ? "discrete_id_rgb"
                              : "srgb_uint8");
        await api.post(`/render-packages/${pkgId}/pass`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // 4. Finalize — server verifies pixel alignment + writes hash.
      const finalized = await api.post(`/render-packages/${pkgId}/finalize`);
      setPkg(finalized.data);
      toast.success("Design prepared for rendering · " + pkgId);
    } catch (e) {
      toast.error(formatErr(e) || "Render Package build failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-border" data-testid="luxe-3d-panel">
      <div className="flex items-center justify-between border-b border-border p-2 gap-2 flex-wrap">
        <div className="overline text-[10px]">3D · Canonical DesignScene</div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[10px] text-muted-foreground">Camera</label>
          <select data-testid="camera-preset"
                  className="luxe-input h-8 text-[11px]"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value)}>
            {Object.keys(CAMERA_PRESETS).map((k) => (
              <option key={k} value={k}>{k.replace(/_/g, " ")}</option>
            ))}
          </select>
          {/* Studio-only Phase 2B-2.5 controls — HIDDEN in Client View. */}
          {!clientView && (
            <button
              type="button"
              data-testid="btn-build-render-package"
              className="btn-outline h-8 text-[11px] disabled:opacity-50"
              onClick={buildPackage}
              disabled={busy || !sceneVersion?.id}>
              {busy ? "Preparing…" : "Prepare for Rendering"}
            </button>
          )}
        </div>
      </div>
      <div className="h-[520px]">
        <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading 3D…</div>}>
          <Luxe3DViewer
            scene={scene || {}}
            project={envProject}
            sceneVersion={sceneVersion}
            pinsByMaterialId={pinsByMaterialId}
            preset={preset}
            onReady={onReady}/>
        </Suspense>
      </div>
      {pkg && !clientView && (
        <div className="border-t border-border p-2 text-[11px]"
             data-testid="render-package-summary">
          <div className="font-medium">Ready to Render</div>
          <div className="text-muted-foreground">
            Preparation complete · {(pkg.output_dimensions?.width)}×{(pkg.output_dimensions?.height)}
          </div>
          <details className="mt-1">
            <summary className="text-[10px] text-muted-foreground cursor-pointer select-none">
              Advanced Render Data
            </summary>
            <div className="mt-1 font-mono text-[10px] text-muted-foreground">
              <div>Package: {pkg.id}</div>
              <div>Status: <span className="text-emerald-700">{pkg.status}</span></div>
              <div>Hash: {(pkg.package_hash || "").slice(0, 16)}…</div>
              <div>Passes: {Object.keys(pkg.passes || {}).join(", ")}</div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
